// Mage shop screen - buy spells for gladiators

import { Canvas } from '../Canvas';
import { CONSTANTS, ScreenState } from '../types';
import { Screen, ScreenContext } from './Screen';
import { GameState, getCurrentTeam } from '../systems/GameState';
import { GAME_CONSTANTS } from '../data/constants';
import { Spell, SPELL_TYPE, SPELL_TYPE_NAMES, ALL_SPELLS, isFriendlySpell } from '../data/spells';
import { GladiatorData, getKnownSpells, getNextSpellSlot } from '../entities/Gladiator';
import { ScrollableList } from '../components/ScrollableList';

export class MageShop implements Screen {
  readonly state = ScreenState.MAGE;
  private context: ScreenContext;
  private gameState: GameState | null = null;
  private selectedGladiatorIndex: number = 0;
  private spellList: ScrollableList<Spell>;

  constructor(context: ScreenContext) {
    this.context = context;
    this.spellList = new ScrollableList<Spell>({
      x: 20,
      y: 55,
      width: 350,
      height: 360,
      itemHeight: 35,
      header: 'Loitsut',
      renderItem: (canvas, spell, x, y, width, _height, _isSelected) => {
        const typeColor = isFriendlySpell(spell) ? '#66aa66' : '#aa6666';
        const typeName = SPELL_TYPE_NAMES[spell.type];

        canvas.drawText(spell.name, x + 10, y + 5, CONSTANTS.MAIN_FONT, '#ffffff');
        canvas.drawText(typeName, x + 10, y + 20, CONSTANTS.MSG_FONT, typeColor);
        canvas.drawText(`${spell.price} kr`, x + width - 100, y + 5, CONSTANTS.MSG_FONT, '#ffcc00');
        canvas.drawText(`Mana: ${spell.manaCost}`, x + width - 100, y + 20, CONSTANTS.MSG_FONT, '#6666ff');
      },
    });
  }

  setGameState(gameState: GameState): void {
    this.gameState = gameState;
    this.selectedGladiatorIndex = 0;
  }

  enter(): void {
    console.log('Entering MageShop');
    this.spellList.setItems(ALL_SPELLS.map((spell, idx) => ({ id: `spell-${idx}`, data: spell })));
    this.spellList.reset();
    this.selectedGladiatorIndex = 0;
  }

  exit(): void {
    console.log('Exiting MageShop');
  }

  update(_deltaTime: number): void {}

  render(canvas: Canvas): void {
    if (!this.gameState) {
      canvas.clear(CONSTANTS.MAIN_BACKGROUND);
      return;
    }

    const team = getCurrentTeam(this.gameState);
    const gladiator = this.getSelectedGladiator();

    canvas.clear('#2a0a2a'); // Dark purple background

    // Header
    this.renderHeader(canvas, team);

    // Spell list (using ScrollableList component)
    this.spellList.render(canvas);

    // Selected spell details
    this.renderSpellDetails(canvas);

    // Gladiator picker and spells
    this.renderGladiatorPicker(canvas);
    this.renderGladiatorSpells(canvas, gladiator);

    // Back button
    this.renderBackButton(canvas);
  }

  private getSelectedGladiator(): GladiatorData | null {
    if (!this.gameState) return null;
    const team = getCurrentTeam(this.gameState);
    if (!team || team.gladiatorIds.length === 0) return null;

    this.clampGladiatorIndex(team.gladiatorIds.length);
    const gladiatorId = team.gladiatorIds[this.selectedGladiatorIndex];
    return this.gameState.gladiators.get(gladiatorId) ?? null;
  }

  private clampGladiatorIndex(maxLength: number): void {
    if (this.selectedGladiatorIndex >= maxLength) {
      this.selectedGladiatorIndex = 0;
    }
  }

  private renderGladiatorPicker(canvas: Canvas): void {
    const pickerX = 390;
    const pickerY = 265;
    const pickerWidth = 385;
    const pickerHeight = 70;

    canvas.drawRect(pickerX, pickerY, pickerWidth, pickerHeight, '#1a0a1a');
    canvas.drawRectOutline(pickerX, pickerY, pickerWidth, pickerHeight, '#6a4a6a');

    const team = getCurrentTeam(this.gameState!);
    if (!team || team.gladiatorIds.length === 0) {
      canvas.drawText('Ei gladiaattoreita', pickerX + pickerWidth / 2, pickerY + pickerHeight / 2,
        CONSTANTS.MAIN_FONT, '#888888', 'center');
      return;
    }

    this.clampGladiatorIndex(team.gladiatorIds.length);
    const gladiatorId = team.gladiatorIds[this.selectedGladiatorIndex];
    const gladiator = this.gameState!.gladiators.get(gladiatorId);
    if (!gladiator) return;

    // Navigation arrows
    const arrowWidth = 30;
    const arrowHeight = 30;
    const arrowY = pickerY + (pickerHeight - arrowHeight) / 2;

    // Previous arrow
    const canGoPrev = this.selectedGladiatorIndex > 0;
    canvas.drawButton(
      'prev-gladiator',
      '◀',
      pickerX + 10,
      arrowY,
      arrowWidth,
      arrowHeight,
      CONSTANTS.BIG_FONT,
      '#ffffff',
      canGoPrev ? '#4a2a8a' : '#2a1a2a',
      () => {
        if (canGoPrev) {
          this.selectedGladiatorIndex--;
        }
      }
    );

    // Portrait and info
    const portrait = this.context.assets.gladiators[gladiator.race.id];
    if (portrait) {
      canvas.drawImage(portrait, pickerX + 50, pickerY + 10, 48, 48);
    }
    canvas.drawText(gladiator.name, pickerX + 110, pickerY + 18, CONSTANTS.GLADIATOR_NAME_FONT, '#ffcc00');
    canvas.drawText(`${gladiator.race.name} | Mana: ${gladiator.mana}/${gladiator.maxMana}`, pickerX + 110, pickerY + 42, CONSTANTS.MSG_FONT, '#aa88aa');

    // Next arrow
    const canGoNext = this.selectedGladiatorIndex < team.gladiatorIds.length - 1;
    canvas.drawButton(
      'next-gladiator',
      '▶',
      pickerX + pickerWidth - arrowWidth - 10,
      arrowY,
      arrowWidth,
      arrowHeight,
      CONSTANTS.BIG_FONT,
      '#ffffff',
      canGoNext ? '#4a2a8a' : '#2a1a2a',
      () => {
        if (canGoNext) {
          this.selectedGladiatorIndex++;
        }
      }
    );

    // Counter
    canvas.drawText(
      `${this.selectedGladiatorIndex + 1} / ${team.gladiatorIds.length}`,
      pickerX + pickerWidth - 60,
      pickerY + pickerHeight - 12,
      CONSTANTS.MSG_FONT,
      '#888888'
    );
  }

  private renderHeader(canvas: Canvas, team: any): void {
    canvas.drawRect(0, 0, CONSTANTS.CANVAS_WIDTH, 45, '#4a1a4a');

    canvas.drawText('Velhojen kauppa', 20, 10, CONSTANTS.BIG_FONT, '#ffffff');

    if (team) {
      canvas.drawText(
        `${GAME_CONSTANTS.MONEY_PREFIX}: ${team.money} ${GAME_CONSTANTS.MONEY_SUFFIX}`,
        CONSTANTS.CANVAS_WIDTH - 20,
        10,
        CONSTANTS.BIG_FONT,
        '#ffffff',
        'right'
      );
    }
  }

  private renderSpellDetails(canvas: Canvas): void {
    const detailX = 390;
    const detailY = 55;
    const detailWidth = 385;
    const detailHeight = 200;

    canvas.drawRect(detailX, detailY, detailWidth, detailHeight, '#1a0a1a');
    canvas.drawRectOutline(detailX, detailY, detailWidth, detailHeight, '#6a4a6a');

    const spell = this.spellList.getSelectedItem();
    if (!spell) return;

    // Spell name
    canvas.drawText(spell.name, detailX + 15, detailY + 15, CONSTANTS.GLADIATOR_NAME_FONT, '#ffcc00');

    // Type
    const typeName = SPELL_TYPE_NAMES[spell.type];
    const typeColor = isFriendlySpell(spell) ? '#66aa66' : '#aa6666';
    canvas.drawText(typeName, detailX + 15, detailY + 40, CONSTANTS.MAIN_FONT, typeColor);

    // Stats
    const statsY = detailY + 70;
    const lineHeight = 22;

    canvas.drawText(`Mana-kustannus: ${spell.manaCost}`, detailX + 15, statsY, CONSTANTS.MAIN_FONT, '#6666ff');

    // Effect description based on type
    let effectText = '';
    switch (spell.type) {
      case SPELL_TYPE.ATK:
        effectText = `Vahinko: ${spell.strength}`;
        break;
      case SPELL_TYPE.HEAL:
        effectText = `Parannus: ${spell.strength} HP`;
        break;
      case SPELL_TYPE.PROTECT:
        effectText = `Suoja: +${spell.strength} (${spell.duration} kierrosta)`;
        break;
      case SPELL_TYPE.MYSTIFICATION:
        effectText = `Sekoittaa kohteen ${spell.duration} kierrokseksi`;
        break;
      case SPELL_TYPE.STR:
        effectText = `Voima: +${spell.strength} (${spell.duration} kierrosta)`;
        break;
      case SPELL_TYPE.STOP:
        effectText = `Pysäyttää kohteen ${spell.duration} kierrokseksi`;
        break;
      case SPELL_TYPE.REMOVE_SPELL:
        effectText = 'Poistaa aktiiviset loitsut kohteesta';
        break;
      case SPELL_TYPE.DRAIN_HEALTH:
        effectText = `Varastaa: ${spell.strength} HP`;
        break;
      case SPELL_TYPE.DRAIN_MANA:
        effectText = `Varastaa: ${spell.strength} manaa`;
        break;
      case SPELL_TYPE.BLESSING:
        effectText = `Osuma +${spell.strength}% (${spell.duration} kierrosta)`;
        break;
      case SPELL_TYPE.CURSE:
        effectText = `Kohteen osuma -${spell.strength}% (${spell.duration} kierrosta)`;
        break;
    }
    canvas.drawText(effectText, detailX + 15, statsY + lineHeight, CONSTANTS.MAIN_FONT, '#cccccc');

    // Target type
    const targetText = isFriendlySpell(spell) ? 'Kohde: Ystävällinen' : 'Kohde: Vihamielinen';
    canvas.drawText(targetText, detailX + 15, statsY + lineHeight * 2, CONSTANTS.MSG_FONT, typeColor);

    // Price
    canvas.drawText(`Hinta: ${spell.price} kr`, detailX + 15, statsY + lineHeight * 3.5, CONSTANTS.MAIN_FONT, '#ffcc00');

    // Buy button
    const team = getCurrentTeam(this.gameState!);
    const gladiator = this.getSelectedGladiator();

    const canBuy = team && gladiator &&
      team.money >= spell.price &&
      getNextSpellSlot(gladiator) >= 0 &&
      !getKnownSpells(gladiator).some(s => s.id === spell.id);

    if (canBuy) {
      canvas.drawButton(
        'buy-spell',
        'Osta loitsu',
        detailX + detailWidth - 120,
        detailY + detailHeight - 45,
        100,
        30,
        CONSTANTS.MAIN_BUTTON_FONT,
        '#ffffff',
        '#4a2a8a',
        () => this.buySpell(spell)
      );
    } else if (gladiator && getKnownSpells(gladiator).some(s => s.id === spell.id)) {
      canvas.drawText('(Jo opittu)', detailX + detailWidth - 100, detailY + detailHeight - 35, CONSTANTS.MSG_FONT, '#666666');
    }
  }

  private renderGladiatorSpells(canvas: Canvas, gladiator: GladiatorData | null): void {
    const spellsX = 390;
    const spellsY = 345;
    const spellsWidth = 385;
    const spellsHeight = 115;

    canvas.drawRect(spellsX, spellsY, spellsWidth, spellsHeight, '#0a0a1a');
    canvas.drawRectOutline(spellsX, spellsY, spellsWidth, spellsHeight, '#6a4a6a');

    if (!gladiator) {
      canvas.drawText('Valitse gladiaattori loitsujen opetteluun', spellsX + spellsWidth / 2, spellsY + spellsHeight / 2,
        CONSTANTS.MAIN_FONT, '#666666', 'center');
      return;
    }

    // Known spells
    const knownSpells = getKnownSpells(gladiator);
    const lineY = spellsY + 10;
    const lineHeight = 20;

    canvas.drawText(`Opitut loitsut (${knownSpells.length}/${GAME_CONSTANTS.MAX_SPELLS}):`, spellsX + 15, lineY, CONSTANTS.MAIN_FONT, '#ccaacc');

    for (let i = 0; i < GAME_CONSTANTS.MAX_SPELLS; i++) {
      const spell = gladiator.knownSpells[i];
      const slotY = lineY + 22 + i * lineHeight;

      if (spell) {
        const typeColor = isFriendlySpell(spell) ? '#66aa66' : '#aa6666';
        canvas.drawText(`${i + 1}. ${spell.name}`, spellsX + 25, slotY, CONSTANTS.MSG_FONT, '#ffffff');
        canvas.drawText(`(${SPELL_TYPE_NAMES[spell.type]})`, spellsX + 200, slotY, CONSTANTS.MSG_FONT, typeColor);
      } else {
        canvas.drawText(`${i + 1}. -tyhjä-`, spellsX + 25, slotY, CONSTANTS.MSG_FONT, '#444444');
      }
    }
  }

  private renderBackButton(canvas: Canvas): void {
    canvas.drawButton(
      'back',
      'Takaisin',
      20,
      CONSTANTS.CANVAS_HEIGHT - 50,
      100,
      35,
      CONSTANTS.MAIN_BUTTON_FONT,
      '#ffffff',
      '#4a2a4a',
      () => this.context.onScreenChange(ScreenState.MAIN_MENU)
    );
  }

  private buySpell(spell: Spell): void {
    const team = getCurrentTeam(this.gameState!);
    const gladiator = this.getSelectedGladiator();
    if (!team || !gladiator) return;

    const slot = getNextSpellSlot(gladiator);
    if (slot < 0) {
      console.log('No spell slots available');
      return;
    }

    if (getKnownSpells(gladiator).some(s => s.id === spell.id)) {
      console.log('Spell already known');
      return;
    }

    if (team.money < spell.price) {
      console.log('Not enough money');
      return;
    }

    // Execute purchase
    team.money -= spell.price;
    gladiator.knownSpells[slot] = spell;
    // Initialize spell attack skill to magic resist
    gladiator.skills.spellAtk[slot] = gladiator.skills.abilities[1];

    console.log(`Learned spell ${spell.name} in slot ${slot + 1}`);
  }

  handleClick(x: number, y: number): void {
    console.log(`MageShop click at (${x}, ${y})`);
  }

  handleKeyDown(key: string): void {
    if (key === 'Escape') {
      this.context.onScreenChange(ScreenState.MAIN_MENU);
    } else {
      this.spellList.handleKeyDown(key);
    }
  }

  handleWheel(x: number, y: number, deltaY: number): void {
    this.spellList.handleWheel(x, y, deltaY);
  }
}
